import { useEffect, useMemo, useState } from "react";
import { api, getProductionId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Lightbulb, Volume2, Shirt, Package, Layers3, Plus, Pencil, Trash2, Save,
  Wrench, AlertCircle, CheckCircle2,
} from "lucide-react";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

const DEPTS = [
  { id: "lighting", label: "Lighting", icon: Lightbulb, tint: "#F59E0B" },
  { id: "sound",    label: "Sound",    icon: Volume2,   tint: "#3B82F6" },
  { id: "costumes", label: "Costumes", icon: Shirt,     tint: "#EC4899" },
  { id: "props",    label: "Props",    icon: Package,   tint: "#10B981" },
  { id: "set",      label: "Set",      icon: Layers3,   tint: "#A855F7" },
];

const STATUSES = [
  { v: "needed",   label: "Needed",     color: "border-amber-400/40 text-amber-300" },
  { v: "sourcing", label: "Sourcing",   color: "border-blue-400/40 text-blue-300" },
  { v: "in_hand",  label: "In hand",    color: "border-emerald-400/40 text-emerald-300" },
  { v: "on_stage", label: "On stage",   color: "border-gold/40 text-gold" },
  { v: "struck",   label: "Struck",     color: "border-white/15 text-zinc-400" },
  { v: "cut",      label: "Cut",        color: "border-red-400/30 text-red-300" },
];

const emptyItem = { name: "", description: "", status: "needed", quantity: 1, cost: "", notes: "", assigned_to: "" };

export default function TechHub() {
  const { user } = useAuth();
  const productionId = getProductionId();
  const canEdit = ["org_admin", "production_staff", "super_admin"].includes(user?.role);
  const [tab, setTab] = useState(DEPTS[0].id);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyItem);

  const load = async () => {
    if (!productionId) return;
    setLoading(true);
    try {
      const params = { production_id: productionId };
      const [i, s] = await Promise.all([
        api.get("/tech-items", { params }),
        api.get("/tech-items/stats", { params }),
      ]);
      setItems(i.data || []);
      setStats(s.data || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [productionId]);

  const visible = useMemo(
    () => items.filter((i) => i.department === tab),
    [items, tab]
  );

  const openCreate = () => { setEditing(null); setForm(emptyItem); setDialogOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name || "",
      description: it.description || "",
      status: it.status || "needed",
      quantity: it.quantity || 1,
      cost: it.cost ?? "",
      notes: it.notes || "",
      assigned_to: it.assigned_to || "",
    });
    setDialogOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const payload = {
      ...form,
      quantity: Number(form.quantity) || 1,
      cost: form.cost === "" ? null : Number(form.cost),
    };
    try {
      if (editing) {
        await api.patch(`/tech-items/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/tech-items", { ...payload, department: tab }, { params: { production_id: productionId } });
        toast.success("Item added");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const del = async (it) => {
    if (!confirm(`Remove "${it.name}"?`)) return;
    await api.delete(`/tech-items/${it.id}`);
    toast.success("Removed");
    load();
  };
  const quickStatus = async (it, v) => {
    await api.patch(`/tech-items/${it.id}`, { status: v });
    load();
  };

  const currentDept = DEPTS.find((d) => d.id === tab);
  const currentStats = stats[tab] || { count: 0, quantity: 0, cost: 0, outstanding: 0, ready: 0 };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="tech-hub">
      <header>
        <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500">Production</div>
        <h1 className="mt-1 font-display text-4xl tracking-tight font-light flex items-center gap-3">
          <Wrench className="w-8 h-8 text-gold" /> Tech Hub
        </h1>
        <p className="mt-1 text-zinc-400 text-sm max-w-2xl">
          Track every physical asset across Lighting, Sound, Costumes, Props, and Set.
          One board per department — but producers get a single roll-up.
        </p>
      </header>

      {/* Roll-up strip across all 5 departments */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="tech-rollup">
        {DEPTS.map((d) => {
          const Icon = d.icon;
          const s = stats[d.id] || { count: 0, outstanding: 0, ready: 0 };
          const active = tab === d.id;
          return (
            <button
              key={d.id}
              onClick={() => setTab(d.id)}
              data-testid={`tech-tab-${d.id}`}
              className={`rounded-lg border p-3 text-left transition-colors ${active ? "border-gold/40 bg-gold/5" : "border-white/10 bg-[#111] hover:border-white/20"}`}
            >
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded grid place-items-center" style={{ backgroundColor: `${d.tint}1a`, color: d.tint }}>
                  <Icon className="w-4 h-4" />
                </span>
                <div className="font-medium text-sm">{d.label}</div>
              </div>
              <div className="mt-2 flex items-baseline justify-between gap-2">
                <div className="font-display text-2xl">{s.count}</div>
                <div className="text-[10px] text-zinc-500 leading-tight text-right">
                  {s.outstanding > 0 ? (
                    <span className="text-amber-300 inline-flex items-center gap-1"><AlertCircle className="w-3 h-3" /> {s.outstanding} to source</span>
                  ) : (
                    <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> All set</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-zinc-400">
          <span className="text-white font-medium">{currentDept.label}</span> ·
          <span className="ml-1">{currentStats.count} item{currentStats.count === 1 ? "" : "s"}</span>
          {currentStats.ready > 0 && <span className="ml-1 text-emerald-400">· {currentStats.ready} ready</span>}
          {currentStats.cost > 0 && <span className="ml-1 text-zinc-500">· ${currentStats.cost.toFixed(2)}</span>}
        </div>
        {canEdit && (
          <button data-testid="tech-new-item-btn" onClick={openCreate} style={goldBtn} className="h-10 px-4 rounded-md font-semibold text-sm hover:brightness-110 inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Add {currentDept.label.slice(0, -1).toLowerCase() === currentDept.label.toLowerCase() ? "item" : currentDept.label.slice(0, -1).toLowerCase()}
          </button>
        )}
      </div>

      {/* Items list */}
      {loading ? (
        <div className="text-zinc-500 text-sm py-10 text-center">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="rounded border border-dashed border-white/10 py-12 text-center text-zinc-500 text-sm" data-testid="tech-empty">
          Nothing tracked in {currentDept.label} yet. {canEdit ? "Click Add to start." : ""}
        </div>
      ) : (
        <div className="rounded-lg border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-[#0A0A0A] text-zinc-500 text-[10px] uppercase tracking-[0.2em]">
                <tr>
                  <th className="text-left p-3">Item</th>
                  <th className="text-left p-3">Qty</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Assigned</th>
                  <th className="text-left p-3">Cost</th>
                  {canEdit && <th className="text-right p-3">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {visible.map((it) => {
                  const st = STATUSES.find((s) => s.v === it.status) || STATUSES[0];
                  return (
                    <tr key={it.id} className="border-t border-white/5" data-testid={`tech-row-${it.id}`}>
                      <td className="p-3">
                        <div className="font-medium">{it.name}</div>
                        {it.description && <div className="text-xs text-zinc-500 mt-0.5 max-w-md line-clamp-2">{it.description}</div>}
                      </td>
                      <td className="p-3 text-zinc-400">{it.quantity ?? 1}</td>
                      <td className="p-3">
                        {canEdit ? (
                          <Select value={it.status} onValueChange={(v) => quickStatus(it, v)}>
                            <SelectTrigger data-testid={`tech-status-${it.id}`} className={`h-8 w-32 bg-transparent text-xs border ${st.color}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#121212] border-white/10 text-white">
                              {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${st.color}`}>{st.label}</span>
                        )}
                      </td>
                      <td className="p-3 text-zinc-400">{it.assigned_to || "—"}</td>
                      <td className="p-3 text-zinc-400">{it.cost != null ? `$${Number(it.cost).toFixed(2)}` : "—"}</td>
                      {canEdit && (
                        <td className="p-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button data-testid={`tech-edit-${it.id}`} onClick={() => openEdit(it)} className="p-1.5 rounded text-zinc-400 hover:text-gold hover:bg-white/5" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                            <button data-testid={`tech-del-${it.id}`} onClick={() => del(it)} className="p-1.5 rounded text-zinc-500 hover:text-red-400 hover:bg-white/5" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg" data-testid="tech-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit item" : `New ${currentDept.label.toLowerCase()} item`}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Track exactly one thing: a cable run, a fixture, a costume piece, a set unit.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input data-testid="tech-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0A0A0A] border-white/10" placeholder="e.g. Source Four 26° · SL1" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea data-testid="tech-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-[#0A0A0A] border-white/10" placeholder="What / where / why" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Qty</Label>
                <Input type="number" min={1} data-testid="tech-qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="bg-[#0A0A0A] border-white/10" />
              </div>
              <div>
                <Label>Cost ($)</Label>
                <Input type="number" step="0.01" data-testid="tech-cost" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="bg-[#0A0A0A] border-white/10" placeholder="Optional" />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="tech-form-status" className="bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Assigned to</Label>
              <Input data-testid="tech-assigned" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="bg-[#0A0A0A] border-white/10" placeholder="Name of the person on it" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea data-testid="tech-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="bg-[#0A0A0A] border-white/10" placeholder="Rental co, delivery date, patch info…" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="tech-save-btn" onClick={save} style={goldBtn} className="h-10 px-4 rounded font-semibold text-sm hover:brightness-110 inline-flex items-center gap-1.5">
              <Save className="w-4 h-4" /> Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
